<!--
  The pending block: the highest-value piece of UI in the system
  (SPECIFICATION 10.2).

  Two states. Left alone it shimmers and shows the intent the agent declared,
  which is how a reader knows the page is mid-sentence rather than finished.
  When a question is OPEN against this block id it stops shimmering, shows the
  question, and offers its options as buttons -- this is the one place a reader
  writes back into the document.
-->
<script lang="ts">
  import { api, describeError } from '../api'
  import type { PMNode, Question } from '../types'

  let { node, questions }: { node: PMNode; questions: Question[] } = $props()

  const id = $derived(node.attrs?.id as string | undefined)
  const question = $derived(questions.find((q) => q.block_id === id && q.status === 'OPEN') ?? null)

  /** Which option is being sent, and what went wrong with the last attempt. */
  let sending = $state<string | null>(null)
  let error = $state<string | null>(null)

  async function answer(option: string) {
    if (!question || sending) return
    sending = option
    error = null
    try {
      await api(`/v1/questions/${question.id}/answer`, {
        method: 'POST',
        body: JSON.stringify({ answer: option }),
      })
      // The stream will bring the answered state back; leaving the button
      // disabled until then reads as "sent", which it is.
    } catch (err) {
      // The rejection used to be swallowed: the button greyed out and nothing
      // else happened, which reads as "accepted". TASK-034.
      error = `That answer did not send: ${describeError(err)}.`
      sending = null
    }
  }
</script>

<div class="pending" class:waiting={question} data-id={id}>
  <span class="label">{question ? 'waiting on an answer' : 'writing'}</span>
  <div>{question ? question.text : (node.attrs?.intent ?? '')}</div>
  {#if question}
    <div class="options">
      {#each question.options ?? [] as option (option)}
        <button type="button" class="option" disabled={sending !== null} onclick={() => answer(option)}>
          {option}
        </button>
      {/each}
    </div>
  {/if}
  {#if error}
    <p class="block-error" role="alert">{error}</p>
  {/if}
</div>
